/**
 * Settings › Receivables data, once TallyPrime is the source: the receivables
 * position as the company reads it, the ageing windows to read it by, and the
 * accounting checks — ongoing (docs/tally/20 §8 "Ongoing accounting checks"
 * and §9 "Advanced receivable settings").
 *
 * Read from what TallyPrime said at the last update (0036), interpreted here
 * with the company's settings by the same domain code the server uses. The
 * figures are what the person may see: someone who sees only their own
 * accounts sees the position of their own accounts, and is told so.
 */
import { useMemo, useState } from 'react';
import { Badge, Button, Disclosure, Panel, Segmented } from '@timely/ui';
import {
    SEVERITY_WORDS, ageHistogram, csvFileName, dateCell, describeFinding, interpretReceivables, rulesFor, summarizeFindings, textCell, toCsv,
    type ReceivablesSettings,
} from '@timely/domain';
import { useFormatters } from '../book/useFormatters';
import { downloadCsv } from '../reports/useReports';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { AgeingEditor } from './AgeingEditor';
import { CheckCounts, ChecksList } from './AccountingChecks';
import { CompositionTable, PositionFigures } from './ReceivablesFigures';
import { useReceivablesSettings } from './useReceivablesSettings';
import {
    acknowledgeFinding, asFinding, toSourceFacts, unacknowledgeFinding, useCompanyFacts, useFindings, type StoredFinding,
} from './useSourceFacts';

export function ReceivablesOverview({ workspaceId, canManage, seesWholeBook }: { workspaceId: string; canManage: boolean; seesWholeBook: boolean }) {
    const facts = useCompanyFacts(workspaceId);
    const settings = useReceivablesSettings(workspaceId);
    const { date } = useFormatters();
    const sourceFacts = useMemo(() => (facts.data ? toSourceFacts(facts.data) : null), [facts.data]);
    const position = useMemo(() => (sourceFacts ? interpretReceivables(sourceFacts, settings.current.settings) : null), [sourceFacts, settings.current.settings]);
    const histogram = useMemo(() => (sourceFacts ? ageHistogram(sourceFacts) : null), [sourceFacts]);
    if (!facts.available || !settings.available) return null;
    if (facts.loading || settings.loading) return <p className="mt-5 text-secondary text-label-3">Adding up…</p>;
    if (!sourceFacts || !position || !histogram) {
        return (
            <Panel className="mt-5" title="Receivables position" bodyClassName="px-5 py-4">
                <p className="text-body text-label-2">The position appears after the next update from TallyPrime.</p>
            </Panel>
        );
    }
    const onSave = canManage
        ? async (next: ReceivablesSettings, reason: string, impact: Record<string, unknown>) => { await settings.save(next, reason, impact); }
        : undefined;
    return (
        <>
            <Panel
                className="mt-5"
                id="receivables-position"
                title="Receivables position"
                description={`As TallyPrime stated it on ${date.day(sourceFacts.asOf)}${seesWholeBook ? '' : ', for the accounts you work'}. Read-only.`}
                bodyClassName="py-4"
            >
                <div className="px-5"><PositionFigures position={position.summary} /></div>
                <h3 className="mt-6 px-5 text-body font-semibold text-label">Ageing</h3>
                <p className="px-5 text-secondary text-label-3 max-w-prose">
                    {settings.current.version > 0
                        ? `Version ${settings.current.version}, saved ${settings.current.changedBy ? `by ${settings.current.changedBy} ` : ''}${settings.current.changedAt ? date.day(settings.current.changedAt) : ''}.`
                        : 'Your company has not chosen how its receivables are aged. Until it does, the windows are 0–45, 46–90, 91–135 and over 135 days by bill date, as TallyPrime’s Age-wise report shows them, and which accounts count as overdue — Late, and the Older-than filters — works exactly as it did before.'}
                    {' '}Saving changes them everywhere at once: Customers, each account, Today and Reports read the same windows.
                </p>
                <div className="mt-3">
                    <AgeingEditor histogram={histogram} position={position.summary} saved={settings.current.settings}
                        chooseBasis={settings.current.version === 0}
                        impact={{ facts: sourceFacts, current: { settings: settings.current.settings, rules: rulesFor(settings.current.version) } }}
                        {...(onSave ? { onSave } : {})} {...(onSave ? {} : { note: 'Somebody who manages receivables data can change how they are aged.' })} />
                </div>
                <div className="mt-5 px-5">
                    <Disclosure title="How the net is made up" summary="Open bills, money in no bill, and credits">
                        <div className="-mx-4"><CompositionTable position={position.summary} /></div>
                    </Disclosure>
                </div>
            </Panel>
            <ChecksPanel workspaceId={workspaceId} canManage={canManage} />
        </>
    );
}

function ChecksPanel({ workspaceId, canManage }: { workspaceId: string; canManage: boolean }) {
    const { workspace } = useActiveWorkspace();
    const { money, date } = useFormatters();
    const [view, setView] = useState<'open' | 'all'>('open');
    const findings = useFindings(workspaceId, view === 'all');
    const [busy, setBusy] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const rows = findings.data ?? [];
    const open = rows.filter(f => !f.resolvedAt);
    const byFingerprint = new Map(rows.map(f => [f.fingerprint, f]));
    const summary = summarizeFindings((view === 'open' ? open : rows).map(asFinding));
    if (!findings.available) return null;

    const act = async (id: string, work: () => Promise<void>) => {
        setBusy(id); setMessage(null);
        try { await work(); await findings.reload(); } catch (e) { setMessage(e instanceof Error ? e.message : 'That could not be done just now.'); } finally { setBusy(null); }
    };
    const text = (f: StoredFinding) => {
        const w = describeFinding(asFinding(f), n => money.exact(n), d => date.day(d));
        return `${w.title} (${w.severity})\nFound: ${w.found}\nWhat it means: ${w.means}\nHow Timely handles it: ${w.handling}\nDoes anyone need to act: ${w.action}\nCan syncing continue: ${w.sync}`;
    };
    const copyAll = async () => {
        const body = open.map(text).join('\n\n');
        try { await navigator.clipboard.writeText(body); setMessage(`Copied ${open.length} check${open.length === 1 ? '' : 's'}, ready to send to your accountant.`); }
        catch { setMessage('Copying is not allowed here; use Export instead.'); }
    };
    // Account names come from TallyPrime and can begin like a formula: every
    // text cell goes through the same protection as the Reports export.
    const exportCsv = () => {
        const described = rows.map(f => ({ f, w: describeFinding(asFinding(f), n => money.exact(n), d => date.day(d)) }));
        const csv = toCsv<(typeof described)[number]>([
            { header: 'Level', cell: ({ f }) => textCell(SEVERITY_WORDS[f.severity]) },
            { header: 'Check', cell: ({ w }) => textCell(w.title) },
            { header: 'Account', cell: ({ f }) => textCell(f.account) },
            { header: 'What Timely found', cell: ({ w }) => textCell(w.found) },
            { header: 'First seen', cell: ({ f }) => dateCell(f.firstSeenAt) },
            { header: 'Resolved', cell: ({ f }) => dateCell(f.resolvedAt) },
            { header: 'Acknowledged by', cell: ({ f }) => textCell(f.acknowledgedAt ? f.acknowledgedBy : null) },
            { header: 'Acknowledged on', cell: ({ f }) => dateCell(f.acknowledgedAt) },
        ], described);
        downloadCsv(csvFileName('accounting checks', workspace.name, date.todayValue()), csv);
    };

    return (
        <Panel
            className="mt-5"
            id="accounting-checks"
            title="Accounting checks"
            description="What Timely noticed in TallyPrime’s figures that affects how clearly they can be collected on. Fixed in TallyPrime, a check resolves at the next update."
            actions={<>
                <Segmented label="Which checks" value={view} onChange={setView} options={[{ value: 'open', label: 'Open' }, { value: 'all', label: 'With resolved' }]} />
                {open.length > 0 && <Button variant="secondary" size="sm" onClick={() => void copyAll()}>Copy</Button>}
                {rows.length > 0 && <Button variant="secondary" size="sm" onClick={exportCsv}>Export</Button>}
            </>}
            bodyClassName="px-5 py-4"
        >
            {message && <p role="status" className="mb-3 text-secondary text-label-2">{message}</p>}
            {findings.loading ? <p className="text-secondary text-label-3">Loading…</p> : (
                <>
                    <CheckCounts summary={summary} />
                    <div className="mt-3">
                        <ChecksList
                            summary={summary}
                            findings={(view === 'open' ? open : rows).map(asFinding)}
                            itemExtra={finding => {
                                const f = byFingerprint.get(finding.fingerprint);
                                if (!f) return null;
                                return (
                                    <span className="flex items-center gap-2 flex-wrap text-caption text-label-3">
                                        {f.resolvedAt && <Badge tone="pos">Resolved {date.day(f.resolvedAt)}</Badge>}
                                        {f.acknowledgedAt && <span>Seen by {f.acknowledgedBy ?? 'someone'}{f.ackNote ? `: “${f.ackNote}”` : ''}</span>}
                                        {canManage && !f.resolvedAt && (f.acknowledgedAt
                                            ? <Button size="sm" variant="quiet" busy={busy === f.id} onClick={() => void act(f.id, () => unacknowledgeFinding(workspaceId, f.id))}>Undo</Button>
                                            : <Button size="sm" variant="quiet" busy={busy === f.id} onClick={() => void act(f.id, () => acknowledgeFinding(workspaceId, f.id, null))}>Mark as seen</Button>)}
                                    </span>
                                );
                            }}
                        />
                    </div>
                    <p className="mt-3 text-caption text-label-3">Marking a check as seen is a note in Timely. It changes nothing in TallyPrime or in the figures.</p>
                </>
            )}
        </Panel>
    );
}
